/**
 * @typedef {import('../MapLibreMap.js').MLD} MLD
 */

/**
 * @typedef {Object} IClusterObject
 * @property {string} clusterID
 * @property {MLD.GeoJSONFeature|null} feature
 * @property {OSM.MapMarker} mapMarker
 * @property {boolean} active
 * @property {IMarkerProps|null} fromMarkerProps
 * @property {IMarkerProps} markerProps
 * @property {number} itemCount
 * @property {string|null=} markerID
 * @property {string|null=} objectID
 */

/**
 * @typedef {Object.<string, IClusterObject>} IClusterObjects
 */


/**
 * @typedef {Object} IMapClusterProps
 * @property {IMarkersProps=} markers
 * @property {IMarkerProps=} marker
 * @property {IMapClusterIconProps=} clusterIcon
 * @property {number=} zIndex
 * @property {number=} buttonZIndex
 * @property {number=} defaultZoomLevel
 * @property {boolean|CountryCode=} withUser
 * @property {boolean=} withPrimaryMarker
 * @property {boolean=} withCountry
 * @property {MLD._CircleLayerSpecification=} layer
 * @property {MLD._GeoJSONSourceSpecification=} source
 * @property {boolean=} sortByNorthWest
 * @property {boolean=} transitionOnClusterChange
 */

window.OSM = window.OSM ?? {};
OSM.MapCluster = class MapCluster {


    /** @type {OSM.MapLibreMap} */
    map;
    /** @type {IMapClusterProps} */
    props;

    /** @type {IClusterObjects} */
    clusterObjects = {}
    layerID = "";
    sourceID = "";
    appliedToMap = false;
    /** @type {MLD._GeoJSONSourceSpecification|null} */
    sourceProps = null;
    /** @type {MLD._CircleLayerSpecification|null} */
    layerProps = null;
    /** @type {IMarkersProps} */
    markersProps = null;
    /** @type {IMarkerProps[]} */
    markerPropsChoiceList = [];
    /** @type {string[]} */
    excludeFromClusterMarkerIDs = [];
    /** @type {Object.<string, string[]>} */
    excludeFromClusterObjectIDs = {};
    /** @type {OSM.DeferredPromise[]} */
    nextUpdatePromises = [];
    waitingForSourceData = false;

    /** @type {IMapProps} */
    coreConfig

    /** @type {string[]|null} */
    requestedVisibleMarkerIDs = null;

    /**
     *
     * @param {OSM.MapLibreMap} map
     * @param {IMapClusterProps} props
     */
    constructor(map, props) {
        this.map = map;
        this.props = props ?? {};
        this.coreConfig = OSM.MapConfigs.core ?? {};
        this.initialize();
    }

    initialize(){
        this.bindFunctions();
        this.applyProps();
        this.applyToMap();
    }

    bindFunctions()
    {

    }

    applyProps() {

        const props = this.props;

        let markersProps = props.markers ?? {};

        if(!Object.keys(markersProps).length){
            return;
        }
        /** @type {MLD._LngLatBounds|null} */
        let bounds= null;

        /** @type {IMarkersProps} */
        const validMarkersProps= {};

        /** @type {IMarkerProps[]} */
        const markerPropsChoiceList= [];

        const sortByNorthWest = this.props.sortByNorthWest ?? (this.coreConfig.cluster?.sortByNorthWest ?? false);
        for(let markerID in markersProps){
            const marker = markersProps[markerID];
            markerID = OSM.isInt(markerID) ? parseInt(markerID) : markerID;
            if(!marker.lngLat || (this.requestedVisibleMarkerIDs && !this.requestedVisibleMarkerIDs.includes(markerID))){
                continue;
            }
            marker.markerID = markerID;
            OSM.deepMergeObjects(marker, props.marker)
            validMarkersProps[markerID] = marker;
            markerPropsChoiceList.push(marker);

            if(sortByNorthWest){

                if(!bounds){
                    bounds = new maplibregl.LngLatBounds(marker.lngLat, marker.lngLat);
                } else{
                    bounds.extend(marker.lngLat);
                }
            }

        }
        markersProps = validMarkersProps;

        if(bounds){
            /** @type {Object.<string, number>} */
            const distances = {};

            const locationManager = OSM.manager.getLocationManager();
            const northWest = bounds.getNorthWest();
            for(let markerID in markersProps){
                const marker = markersProps[markerID];
                distances[marker.markerID] = locationManager.getDistanceInMetresBetween(marker.lngLat, northWest);
            }

            markerPropsChoiceList.sort((a, b) => {
                const aDistance = distances[a.markerID] ?? null;
                const bDistance = distances[b.markerID] ?? null;
                if(aDistance !== null && bDistance === null){
                    return  -1;
                }
                if(aDistance === null && bDistance !== null){
                    return  1;
                }
                if(aDistance !== null){
                    if(aDistance < bDistance){
                        return  -1;
                    }
                    if(aDistance > bDistance){
                        return  1;
                    }
                }
                let aIndex = markerPropsChoiceList.indexOf(a);
                let bIndex = markerPropsChoiceList.indexOf(b);

                return aIndex <= bIndex ? -1 : 1;
            });
        }

        this.markersProps = markersProps;
        this.markerPropsChoiceList = markerPropsChoiceList;

        /** @type {IMapClusterProps|null} */
        const configProps = this.coreConfig.cluster ?? null;

        this.layerID = props.layer?.id ?? configProps?.layer?.id ?? "none";
        this.sourceID = props.layer?.source ?? configProps?.layer?.source ?? "none";

        this.updateSource();

        this.layerProps = {
            ...configProps?.layer,
            ...props.layer,
            id: this.layerID,
            type: 'circle',
            source: this.sourceID,
            paint: {
                ...configProps?.layer?.paint,
                ...props.layer?.paint,
                'circle-opacity': 0
            }
        }

        this.waitingForSourceData = true;
    }

    updateSource() {

        const props = this.props;

        /**
         *
         * @returns {(GeoJSON.Feature|any)[]}
         */
        const createClusterFeatures = () =>
        {
            /** @type {GeoJSON.Feature[]} */
            const features = [];
            for(let props of this.markerPropsChoiceList){
                const markerID = props.markerID;
                if(this.excludeFromClusterMarkerIDs.includes(markerID)){
                    continue;
                }
                const markerExcludedObjectIDs = this.excludeFromClusterObjectIDs[markerID] ?? []

                const objectIDs = (props.objectIDs ?? []).slice();
                if(objectIDs.length){
                    for(let objectID of markerExcludedObjectIDs){
                        let index = objectIDs.indexOf(objectID);
                        if(index !== -1){
                            objectIDs.splice(index, 1);
                        }
                    }
                    if(!objectIDs.length){
                        continue;
                    }
                }
                const lngLat = maplibregl.LngLat.convert(props.lngLat);
                features.push({
                    type: "Feature",
                    properties: {
                        markerID: markerID,
                        childCount: objectIDs.length ? objectIDs.length : 1,
                        latitude: lngLat.lat,
                        longitude: lngLat.lng

                    },
                    geometry: {
                        type: "Point",
                        coordinates: [
                            lngLat.lng,
                            lngLat.lat
                        ]
                    }
                })
            }
            return features;
        }
        const features = createClusterFeatures();

        /** @type {IMapClusterProps|null} */
        const configProps = this.coreConfig.cluster ?? null;

        this.sourceProps = {
            ...configProps?.source,
            ...props.source,
            type: props.source?.type ?? (configProps?.source?.type ?? "geojson"),
            data: {
                type: "FeatureCollection",
                features: features
            },
            cluster: props.source?.cluster ?? configProps?.source?.cluster,
            clusterMaxZoom: props.source?.clusterMaxZoom ?? configProps?.source?.clusterMaxZoom,
            clusterRadius: props.source?.clusterRadius ?? configProps?.source?.clusterRadius,
            clusterProperties: {
                ...configProps.source?.clusterProperties,
                ...props.source?.clusterProperties,
                itemCount: ["+", ["get", "childCount"]],
                minLatitude: ["min", ["get", "latitude"]],
                maxLatitude: ["max", ["get", "latitude"]],
                minLongitude: ["min", ["get", "longitude"]],
                maxLongitude: ["max", ["get", "longitude"]]
            }
        }

        if(!this.appliedToMap){
            return;
        }

        this.map.map.removeLayer(this.layerID)
        this.map.map.removeSource(this.sourceID)
        this.map.map.addSource(this.sourceID, this.sourceProps)
        this.map.map.addLayer(this.layerProps);
    }

    updateClusters()
    {
        if(this.waitingForSourceData || !this.appliedToMap){
            return;
        }

        const map = this.map;
        const mapMap = map.map;

        if (!mapMap.getSource(this.sourceID) || !mapMap.isSourceLoaded(this.sourceID))
        {
            return;
        }
        const features = mapMap.querySourceFeatures(this.sourceID);

        /** @type {string[]} */
        const activeClusterIDs = [];

        /**
         *
         * @param clusterID {string}
         * @param feature {MLD.GeoJSONFeature|null}
         * @param latitude {number}
         * @param longitude {number}
         * @param markerID {string|null=}
         * @param itemCount {number=}
         * @param objectID {string|null=}
         */
        const requireActiveClusterObject = (clusterID, feature, latitude, longitude, markerID, itemCount, objectID) => {

            markerID = markerID ?? null;
            itemCount = itemCount ?? 1;
            objectID = objectID ?? null;

            if(activeClusterIDs.includes(clusterID)){
                return;
            }
            activeClusterIDs.push(clusterID);

            /**
             *
             * @returns {IMarkerProps|null}
             */
            const getFromMarkerProps = () => {
                return markerID && this.props.markers ? (this.props.markers[markerID] ?? null) : null;
            }

            /** @type {IClusterObject|null} */
            let clusterObject = this.clusterObjects[clusterID] ?? null;

            const useClusterIcon = !markerID || (itemCount !== 1);


            const fromMarkerProps = getFromMarkerProps();

            /**
             *
             * @param includeRelated {boolean=}
             * @returns {string[]}
             */
            const getObjectIDs = (includeRelated) => {
                const fromObjectIDs = fromMarkerProps?.objectIDs ?? [];
                return !!includeRelated || !objectID || !fromObjectIDs.includes(objectID) ? fromObjectIDs : [objectID]
            }

            if(clusterObject){
                if(
                    (itemCount !== clusterObject.itemCount) ||
                    (fromMarkerProps !== clusterObject?.fromMarkerProps)
                ){
                    clusterObject.mapMarker.removeFromMap()
                    clusterObject = null;
                }
            }

            /**
             *
             * @returns {(e: MLD._MapMouseEvent) => string}
             */
            const getOnClick = () => {
                if(!markerID){

                    return (
                        /**
                         *
                         * @param _ {MLD._MapMouseEvent}
                         * @returns {string}
                         */
                            (_) => {
                            const clusterMarker = clusterObject?.mapMarker?? null;
                            /** @type {number|null} */
                            const north = feature?.properties?.maxLatitude ?? null
                            /** @type {number|null} */
                            const south = feature?.properties?.minLatitude ?? null
                            /** @type {number|null} */
                            const east = feature?.properties?.maxLongitude ?? null
                            /** @type {number|null} */
                            const west = feature?.properties?.minLongitude ?? null
                            /** @type {number|null} */
                            let zoomLevel = null;
                            if(north !== null && south !== null && east !== null && west !== null){
                                const lngLatBounds = new maplibregl.LngLatBounds(new maplibregl.LngLat(west, south), new maplibregl.LngLat(east, north));
                                zoomLevel = map.getZoomLevelForBounds(lngLatBounds, true);
                                const currentZoomLevel = map.map.getZoom() ?? null;
                                if(zoomLevel !== null && currentZoomLevel !== null){
                                    zoomLevel --;
                                    const clusterMaxZoomLevel = this.sourceProps?.clusterMaxZoom ?? null;
                                    if(clusterMaxZoomLevel){
                                        zoomLevel = Math.min(zoomLevel, clusterMaxZoomLevel + 1);
                                    }
                                    zoomLevel = Math.max(zoomLevel, currentZoomLevel + 1)
                                }
                            }
                            if(!clusterMarker){
                                return "";
                            }
                            map.zoomToMarker(clusterMarker, zoomLevel);
                            return "";
                        })
                }
                if(getObjectIDs().length > 1){
                    return (
                        /**
                         *
                         * @param e {MLD._MapMouseEvent}
                         * @returns {string}
                         */
                            (e) => {
                            const fromMarkerProps = getFromMarkerProps();
                            const markerID = fromMarkerProps?.markerID ?? null;
                            const objectIDs = getObjectIDs();
                            if(markerID && objectIDs.length){
                                /** @type {string|null} */
                                let objectID = null;
                                if(!this.excludeFromClusterMarkerIDs.length){
                                    if(Object.keys(this.excludeFromClusterObjectIDs).length === 1){
                                        const excludeObjectIDs = this.excludeFromClusterObjectIDs[markerID] ?? [];
                                        if(excludeObjectIDs.length === 1){
                                            for(let i = 1; i < objectIDs.length; i++){
                                                if(objectIDs[i - 1] === excludeObjectIDs[0]){
                                                    objectID = objectIDs[i];
                                                }
                                            }
                                        }
                                    }
                                }
                                objectID = objectID ?? objectIDs[0];
                                map.selectClusterMarker(markerID, objectID, !!fromMarkerProps?.centerOnClick, true, true);
                            }
                            if(fromMarkerProps?.onClick){
                                fromMarkerProps.onClick(e);
                            }
                        }
                    )
                }
                return (
                    /**
                     *
                     * @param e {MLD._MapMouseEvent}
                     * @returns {string}
                     */
                        (e) => {
                        const fromMarkerProps = getFromMarkerProps();
                        if(getObjectIDs(true).length < 2){
                            this.excludeMarkerFromCluster(markerID, null, true).then()
                        }
                        if(fromMarkerProps?.onClick){
                            fromMarkerProps.onClick(e);
                        }
                        return "";
                    }
                )
            }

            /**
             *
             * @param itemCount {number}
             * @returns {OSM.MapClusterIcon}
             */
            const createClusterIcon = (itemCount) => {
                /** @type {IMapClusterIconProps} */
                const props = {
                    ...this.props.clusterIcon,
                    itemCount: itemCount,
                }
                return new OSM.MapClusterIcon(props);
            }

            /** @type {MLD._LngLat} */
            const lngLat = new maplibregl.LngLat(longitude, latitude);

            if(!clusterObject){

                /** @type {IMarkerProps} */
                let markerProps;

                /** @type {IMarkerProps} */
                const fromConfigMarkerProps = this.coreConfig.cluster?.marker ?? null;

                /** @type {number} */
                let defaultZIndex;
                if((fromMarkerProps?.defaultZIndex ?? null) !== null){
                    defaultZIndex = fromMarkerProps.defaultZIndex;
                } else if(!useClusterIcon){
                    defaultZIndex = fromConfigMarkerProps?.defaultZIndex;
                } else if(!!markerID){
                    if((this.props.buttonZIndex ?? null) !== null){
                        defaultZIndex = this.props.buttonZIndex;
                    } else {
                        defaultZIndex = this.coreConfig.cluster?.buttonZIndex;
                    }
                } else {
                    if(this.props.zIndex){
                        defaultZIndex = this.props.zIndex;
                    } else {
                        defaultZIndex = this.coreConfig.cluster?.zIndex;
                    }
                }
                /** @type {boolean} */
                let centerOnClick;
                if((fromMarkerProps?.centerOnClick ?? null) !== null){
                    centerOnClick = fromMarkerProps.centerOnClick;
                } else if(!!markerID){
                    centerOnClick = fromConfigMarkerProps?.centerOnClick ?? false
                } else {
                    centerOnClick = false;
                }

                const objectIDs = getObjectIDs();
                markerProps = {
                    ...fromMarkerProps,
                    markerOptions: fromMarkerProps?.markerOptions,
                    lngLat: lngLat,
                    onClick: getOnClick(),
                    popupContent: objectIDs.length < 2 ? fromMarkerProps?.popupContent ?? null : null,
                    markerID: markerID,
                    objectIDs: objectIDs,
                    defaultZIndex: defaultZIndex,
                    centerOnClick: centerOnClick,
                    cursor: !markerID ? "grab" : undefined,
                };

                if(useClusterIcon)
                {
                    markerProps.icon = createClusterIcon(itemCount);
                } else {
                    markerProps.iconProps = {
                        ...fromMarkerProps?.iconProps,
                        color: fromMarkerProps?.iconProps?.color ?? this.coreConfig.cluster?.marker?.iconProps?.color,
                        innerColor: fromMarkerProps?.iconProps?.innerColor ?? this.coreConfig.cluster?.marker?.iconProps?.innerColor,
                        strokeColor: fromMarkerProps?.iconProps?.strokeColor ?? this.coreConfig.cluster?.marker?.iconProps?.strokeColor,
                        imagePath: fromMarkerProps?.iconProps?.imagePath !== undefined ? fromMarkerProps?.iconProps?.imagePath : this.coreConfig.cluster?.marker?.iconProps?.imagePath
                    }
                }

                const mapMarker = new OSM.MapMarker(map, "cluster", markerProps);
                if(this.appliedToMap){
                    mapMarker.addToMap();
                }

                clusterObject = {
                    clusterID: clusterID,
                    feature: feature,
                    mapMarker: mapMarker,
                    fromMarkerProps: fromMarkerProps,
                    markerProps: markerProps,
                    active: true,
                    itemCount: itemCount,
                    markerID: markerID,
                    objectID: objectID
                }
                this.clusterObjects[clusterID] = clusterObject;
            } else {

                clusterObject.active = true;
                if(this.appliedToMap){
                    clusterObject.mapMarker.addToMap();
                }
                const markerLngLat = maplibregl.LngLat.convert(clusterObject.mapMarker.props.lngLat);
                if(
                    latitude !== markerLngLat.lat ||
                    longitude !== markerLngLat.lng
                ){
                    clusterObject.mapMarker.props.lngLat = lngLat
                    clusterObject.mapMarker.setLngLat(lngLat)
                }
                clusterObject.feature = feature;
            }
        }

        for (const feature of features) {
            const props = feature.properties;
            const markerID= props.markerID ? props.markerID.toString() : null;
            const clusterID = markerID ?? (props.cluster_id ? "cluster_"+props.cluster_id : "");
            /** @type {GeoJSON.Point} */
            const geometry = feature.geometry
            const coords = geometry.coordinates;
            const itemCount = parseInt(props.itemCount ?? (props.childCount ?? "1"));
            const latitude = coords[1];
            const longitude = coords[0];
            requireActiveClusterObject(clusterID, feature, latitude, longitude, markerID, itemCount);
        }

        /** @type {IMarkersProps} */
        const excludedFromClusterMarkerProps = {};
        /** @type {Object.<string, string>} */
        const clusterIdObjectIDs = {};
        for(let markerID of this.excludeFromClusterMarkerIDs){
            /** @type {IMarkerProps|null} */
            const markerProps = this.markersProps[markerID] ?? null;
            if(markerProps){
                excludedFromClusterMarkerProps[markerID] = markerProps;
            }
        }

        for(let markerID in this.excludeFromClusterObjectIDs){
            if(this.excludeFromClusterMarkerIDs.includes(markerID)){
                continue
            }
            const objectIDs = this.excludeFromClusterObjectIDs[markerID]
            const markerProps = this.markersProps[markerID];
            if(objectIDs && markerProps.objectIDs){
                for(let objectID of objectIDs){
                    if(markerProps.objectIDs.includes(objectID)){
                        const clusterID = this.getClusterID(markerID, objectID);
                        clusterIdObjectIDs[clusterID] = objectID;
                        excludedFromClusterMarkerProps[clusterID] = markerProps;
                    }
                }
            }
        }

        for(let clusterID in excludedFromClusterMarkerProps){
            const markerProps= excludedFromClusterMarkerProps[clusterID];
            const markerID = markerProps.markerID ?? "";
            const objectID = clusterIdObjectIDs[clusterID] ?? null;
            const lngLat = maplibregl.LngLat.convert(markerProps.lngLat);
            requireActiveClusterObject(clusterID, null, lngLat.lat, lngLat.lng, markerID, 1, objectID);
        }

        for(let clusterID in this.clusterObjects){
            const clusterObject = this.clusterObjects[clusterID];
            if(clusterObject.active && !activeClusterIDs.includes(clusterID)){
                clusterObject.active = false;
                clusterObject.mapMarker.removeFromMap();
            }
        }
    }

    /**
     *
     * @param requestedVisibleMarkerIDs {string[]|null}
     */
    showDefinedClusterMarkers(requestedVisibleMarkerIDs)
    {
        const map = this.map;
        const transitionOnClusterChange = this.props.transitionOnClusterChange ?? (this.coreConfig.cluster?.transitionOnClusterChange ?? false);
        if(transitionOnClusterChange){
            map.showTransition();
        }
        map.deselectSelectedMarker();
        this.excludeFromClusterMarkerIDs.length = 0;
        this.requestedVisibleMarkerIDs = requestedVisibleMarkerIDs;
        this.applyProps();
    }

    /**
     *
     * @returns {boolean}
     */
    canCycleClusterMarkers(){
        const choiceList = this.markerPropsChoiceList
        return choiceList.length > 1 || (choiceList.length === 1 && choiceList[0].objectIDs && choiceList[0].objectIDs.length > 1);
    }

    showPreviousClusterMarker(){
        /** @type {IMarkerProps[]} */
        const choiceList = this.markerPropsChoiceList;
        const map = this.map;

        /** @type {string|null} */
        let fromMarkerID;
        if(map.selectedMarker){
            fromMarkerID = map.selectedMarker.markerID;
        } else {
            /** @type {IMarkerProps|null} */
            const fromMarker = choiceList.length ? choiceList[0] : null;
            fromMarkerID = fromMarker?.markerID ?? null;
        }
        let fromMarker = fromMarkerID ? (this.markersProps[fromMarkerID] ?? null) : null;
        fromMarker = fromMarker ? fromMarker : choiceList[0];
        if(!fromMarker){
            return;
        }
        fromMarkerID = fromMarker.markerID;
        /** @type {string|null} */
        let fromObjectID = null;
        const fromObjectIDs = fromMarker.objectIDs ?? [];
        if(fromObjectIDs.length){
            fromObjectID = this.getSelectedObjectID(fromMarkerID);
            fromObjectID = fromObjectID && fromObjectIDs.includes(fromObjectID) ? fromObjectID : fromObjectIDs[0];
        }
        if(fromObjectID){
            const index = fromObjectIDs.indexOf(fromObjectID);
            if(index > 0){
                const toObjectID = fromObjectIDs[index - 1];
                map.selectClusterMarker(fromMarkerID, toObjectID, true, true);
                return;
            }
        }
        let fromMarkerIndex = -1;
        for(let i = 0; i < choiceList.length; i ++){
            const checkMarker = choiceList[i];
            if(checkMarker.markerID === fromMarkerID){
                fromMarkerIndex = i;
                break;
            }
        }

        if(fromMarkerIndex < 0){
            fromMarkerIndex = 0;
            if(fromObjectIDs.length){
                fromObjectID = fromObjectIDs[0]
            }
        }
        /** @type {IMarkerProps} */
        const toMarker = fromMarkerIndex !== 0 ? choiceList[fromMarkerIndex - 1] : choiceList[choiceList.length - 1];
        const toMarkerID = toMarker.markerID;

        const toObjectIDs = toMarker.objectIDs ?? [];
        /** @type {string|null} */
        const toObjectID = toObjectIDs.length ? toObjectIDs[toObjectIDs.length - 1] : null;
        if((fromMarkerID === toMarkerID) && (fromObjectID === toObjectID)){
            return;
        }
        map.selectClusterMarker(toMarkerID, toObjectID, true, true);
    }

    showNextClusterMarker(){
        /** @type {IMarkerProps[]} */
        const choiceList = this.markerPropsChoiceList;
        const map = this.map;

        /** @type {string|null} */
        let fromMarkerID;
        if(map.selectedMarker){
            fromMarkerID = map.selectedMarker.markerID;
        } else {
            /** @type {IMarkerProps|null} */
            const fromMarker = choiceList.length ? choiceList[0] : null;
            fromMarkerID = fromMarker?.markerID ?? null;
        }
        let fromMarker = fromMarkerID ? this.markersProps[fromMarkerID] : null;
        fromMarker = fromMarker ? fromMarker : choiceList[0];
        if(!fromMarker){
            return;
        }
        fromMarkerID = fromMarker.markerID;
        /** @type {string|null} */
        let fromObjectID = null;
        const fromObjectIDs = fromMarker.objectIDs ?? [];
        if(fromObjectIDs.length){
            fromObjectID = this.getSelectedObjectID(fromMarkerID);
            fromObjectID = fromObjectID && fromObjectIDs.includes(fromObjectID) ? fromObjectID : fromObjectIDs[0];
        }
        if(fromObjectID){
            const index = fromObjectIDs.indexOf(fromObjectID);
            if(index < fromObjectIDs.length - 1){
                const toObjectID = fromObjectIDs[index + 1];
                map.selectClusterMarker(fromMarkerID, toObjectID, true, true);
                return;
            }
        }
        let fromMarkerIndex = -1;
        for(let i = 0; i < choiceList.length; i ++){
            const checkMarker = choiceList[i];
            if(checkMarker.markerID === fromMarkerID){
                fromMarkerIndex = i;
                break;
            }
        }
        if(fromMarkerIndex < 0){
            fromMarkerIndex = choiceList.length - 1;
            if(fromObjectIDs.length){
                fromObjectID = fromObjectIDs[fromObjectIDs.length - 1]
            }
        }
        /** @type {IMarkerProps} */
        const toMarker = fromMarkerIndex !== choiceList.length - 1 ? choiceList[fromMarkerIndex + 1] : choiceList[0];
        const toMarkerID = toMarker.markerID;

        const toObjectIDs = toMarker.objectIDs ?? [];
        /** @type {string|null} */
        const toObjectID = toObjectIDs.length ? toObjectIDs[0] : null;
        if((fromMarkerID === toMarkerID) && (fromObjectID === toObjectID)){
            return;
        }
        map.selectClusterMarker(toMarkerID, toObjectID, true, true);
    }

    /**
     *
     * @param userPosition {MLD._LngLat|null=}
     * @param primaryMarkerPosition {MLD._LngLat|null=}
     * @param countryBounds {MLD._LngLatBounds|null=}
     * @param userCountryCode {CountryCode|null=}
     * @returns {MLD._LngLatBounds|null}
     */
    getClusterBounds(userPosition, primaryMarkerPosition, countryBounds, userCountryCode)
    {
        /** @type {MLD._LngLat[]} */
        const clusterPositions= [];
        for(let markerProps of this.markerPropsChoiceList){
            if(markerProps.outOfBounds || !markerProps.lngLat){
                continue;
            }
            const lngLat = maplibregl.LngLat.convert(markerProps.lngLat);
            clusterPositions.push(new maplibregl.LngLat(lngLat.lng, lngLat.lat))
        }
        /** @type {boolean|CountryCode} */
        const withUser = this.props.withUser ?? (this.coreConfig.cluster?.withUser ?? false);
        const withPrimaryMarker = this.props.withPrimaryMarker ?? (this.coreConfig.cluster?.withPrimaryMarker ?? false);
        const withCountry = this.props.withCountry ?? (this.coreConfig.cluster?.withCountry ?? false);
        if(withUser && userPosition){
            if(withUser === true || withUser === userCountryCode){
                clusterPositions.push(userPosition)
            }
        }
        if(withPrimaryMarker && primaryMarkerPosition)
        {
            clusterPositions.push(primaryMarkerPosition)
        }
        if(withCountry && countryBounds){
            for(let i = 0; i < clusterPositions.length; i++ ){
                const position = clusterPositions[i];
                if(!countryBounds.intersects(new maplibregl.LngLatBounds(position, position))){
                    clusterPositions.splice(i, 1);
                    i --;
                }
            }
        }
        if(!clusterPositions.length){
            return null;
        }
        const bounds = new maplibregl.LngLatBounds(clusterPositions[0], clusterPositions[0]);
        for(let i = 1; i < clusterPositions.length; i++ ){
            bounds.extend(clusterPositions[i])
        }

        return bounds;
    }

    /**
     *
     * @param focusZoomLevel {number}
     * @param primaryMarkerZoomLevel {number|null=}
     * @param countryZoomLevel {number|null=}
     * @returns {number}
     */
    getActiveFocusZoomLevel(focusZoomLevel, primaryMarkerZoomLevel, countryZoomLevel)
    {
        const clusterFocusZoomLevel = focusZoomLevel > 2 ? focusZoomLevel - 1 : focusZoomLevel;

        if(!!this.props.withPrimaryMarker){
            if(primaryMarkerZoomLevel)
            {
                return primaryMarkerZoomLevel;
            }
        }
        if(!!this.props.withCountry){
            if(countryZoomLevel)
            {
                if(clusterFocusZoomLevel <= countryZoomLevel){
                    return countryZoomLevel;
                }
                return Math.min(countryZoomLevel + 4, clusterFocusZoomLevel);
            }
        }
        return clusterFocusZoomLevel
    }

    /**
     *
     * @param focus {MapFocus}
     * @returns {boolean}
     */
    containsFocus(focus)
    {
        if(focus === "primary_marker" && (this.props.withPrimaryMarker ?? false) ){
            return true;
        }
        if(focus === "user" && (this.props.withUser ?? false) ){
            return true;
        }
        return focus === "country" && (this.props.withCountry ?? false);

    }

    /**
     *
     * @param forMarkerID
     * @returns {string|null}
     */
    getSelectedObjectID(forMarkerID){
        if(!forMarkerID){
            const keys = Object.keys(this.excludeFromClusterObjectIDs);
            forMarkerID = keys.length === 1 ? keys[0] : null;
        }
        const objectIDs = forMarkerID ? (this.excludeFromClusterObjectIDs[forMarkerID] ?? []) : [];
        return objectIDs.length === 1 ? objectIDs[0] : null;
    }

    /**
     *
     * @param markerID {string}
     * @param objectID {(string|null)=}
     * @param ifNotActive {boolean=}
     * @param addToExclusion {boolean=}
     * @returns {Promise<MapMarker|null>}
     */
    async excludeMarkerFromCluster(markerID, objectID, ifNotActive, addToExclusion)
    {

        if(ifNotActive){
            const clusterObject = markerID ? this.clusterObjects[markerID] : null
            const clusterMarker = clusterObject?.mapMarker ?? null;
            if(clusterMarker && clusterObject?.active){
                return clusterMarker;
            }
        }

        objectID = objectID ?? null;
        addToExclusion = !!addToExclusion;

        const markerProps = this.markersProps[markerID];
        if(objectID){
            const objectIDs = markerProps.objectIDs ?? [];
            if((objectIDs.length === 1) && (objectIDs[0] === objectID)){
                objectID = null;
            }
        }
        if(!markerProps || (objectID && !markerProps.objectIDs?.includes(objectID)) || !this.sourceProps){
            return null;
        }

        /**
         *
         * @param requireSingleExclusion {boolean=}
         * @returns {OSM.MapMarker|null}
         */
        const getMarker = (requireSingleExclusion) => {
            requireSingleExclusion = !!requireSingleExclusion;
            const clusterID = this.getClusterID(markerID, objectID);
            const clusterObject = this.clusterObjects[clusterID];
            if(clusterObject){
                let valid = false;
                if(objectID){
                    const markerExcludedObjectIDs = this.excludeFromClusterObjectIDs[markerID] ?? []
                    if(markerExcludedObjectIDs.includes(objectID)){
                        if(!requireSingleExclusion || ((markerExcludedObjectIDs.length === 1) && !this.excludeFromClusterMarkerIDs.length)){
                            valid = true;
                        }
                    }
                } else {
                    if(this.excludeFromClusterMarkerIDs.includes(markerID)){
                        if(!requireSingleExclusion || ((this.excludeFromClusterMarkerIDs.length === 1) && !Object.keys(this.excludeFromClusterObjectIDs).length)){
                            valid = true;
                        }
                    }
                }
                if(valid){
                    return clusterObject.mapMarker;
                }
            }
            return null;
        }
        const marker = getMarker(!addToExclusion);
        if( marker ){
            return marker;
        }
        if(!addToExclusion){
            this.excludeFromClusterMarkerIDs.length = 0;
            this.excludeFromClusterObjectIDs = {};
        }
        if(!objectID){
            if(!this.excludeFromClusterMarkerIDs.includes(markerID)){
                this.excludeFromClusterMarkerIDs.push(markerID);
            }
        } else {

            let markerIndex = this.excludeFromClusterMarkerIDs.indexOf(markerID);
            if(markerIndex !== -1){
                this.excludeFromClusterMarkerIDs.splice(markerIndex, 1);
            }

            if(!this.excludeFromClusterObjectIDs[markerID]){
                this.excludeFromClusterObjectIDs[markerID] = [];
            }
            if(!this.excludeFromClusterObjectIDs[markerID].includes(objectID)){
                this.excludeFromClusterObjectIDs[markerID].push(objectID);
            }
        }

        const promise = OSM.createDeferredPromise();
        this.nextUpdatePromises.push(promise);
        this.updateSource();

        return promise.then(() =>{
            return getMarker();
        });
    }

    /**
     *
     * @param markerID {string}
     * @param objectID {string|null=}
     * @returns {string}
     */
    getClusterID(markerID, objectID){
        objectID = objectID ?? null;
        return !objectID ? markerID : markerID + "_object_" + objectID;
    }

    /**
     *
     * @param markerID {string|null}
     * @param andActive {boolean=}
     * @returns {boolean}
     */
    isClusterMarker(markerID, andActive)
    {
        const clusterObject = markerID ? this.clusterObjects[markerID] : null
        return !!clusterObject && (!andActive || clusterObject.active);
    }

    /**
     *
     * @param marker {MapMarker}
     * @returns {Promise<void>}
     */
    async deselectMarker(marker)
    {
        let didDeselect = false;
        if(marker.props.markerID && this.props.markers && this.props.markers[marker.props.markerID]){
            let markerIndex = this.excludeFromClusterMarkerIDs.indexOf(marker.props.markerID);
            if(markerIndex !== -1){
                this.excludeFromClusterMarkerIDs.splice(markerIndex, 1);
                didDeselect = true;
            }
            if(marker.props.objectIDs?.length === 1){
                const objectID = marker.props.objectIDs[0];
                const markerExcludedObjectIDs = this.excludeFromClusterObjectIDs[marker.props.markerID] ?? []
                let objectIndex = markerExcludedObjectIDs.indexOf(objectID);
                if(objectIndex !== -1){
                    markerExcludedObjectIDs.splice(objectIndex, 1);
                    if(!markerExcludedObjectIDs.length){
                        delete this.excludeFromClusterObjectIDs[marker.props.markerID]
                    }
                    didDeselect = true;
                }
            }
        }
        if(!didDeselect){
            return;
        }

        const promise = OSM.createDeferredPromise();
        this.nextUpdatePromises.push(promise);
        this.updateSource();
        return promise;
    }

    removeFromMap(){
        if(!this.appliedToMap || this.map.cluster !== this){
            return;
        }
        for(let clusterID in this.clusterObjects) {
            const clusterObject = this.clusterObjects[clusterID];
            clusterObject.mapMarker.removeFromMap();
        }
        this.map.map.removeLayer(this.layerID)
        this.map.map.removeSource(this.sourceID)
        this.map.cluster = null;
    }

    applyToMap(){
        if(this.appliedToMap || !this.map.styleLoaded || !this.map.didFindInitialFocus){
            return;
        }
        if(!this.map.cluster){
            this.map.cluster = this;
        }
        if(this.map.cluster !== this){
            return;
        }
        this.appliedToMap = true;
        if(!this.sourceID){
            this.map.checkMapReady()
            return;
        }

        const mapMap = this.map.map
        if (!mapMap)
        {
            return;
        }

        mapMap.on("move",
            /**
             *
             * @param _ {MLD.MapLibreEvent<MouseEvent | TouchEvent | undefined>}
             */
            (_) =>
            {
                this.updateClusters();
            });
        mapMap.on("moveend",
            /**
             *
             * @param _ {MLD.MapLibreEvent<MouseEvent | TouchEvent | undefined>}
             */
            (_) =>
            {
                this.updateSource();
            });
        mapMap.on("sourcedata",
            /**
             *
             * @param event {MLD.MapSourceDataEvent}
             */
            (event) =>
            {
                //n.b. I added 'event.sourceDataType !== "metadata"' because this was being triggered too early when excluding markers.
                //If any other side effects from this appear then I will have to only use this condition in specific circumstances
                if(event.isSourceLoaded  && (event.sourceId === this.sourceID) && (event.sourceDataType !== "metadata")){
                    this.waitingForSourceData = false;
                    this.updateClusters();

                    for(let promise of this.nextUpdatePromises){
                        promise.resolve(null);
                    }
                    this.nextUpdatePromises.length = 0;
                    this.map.checkMapReady()
                }
            });
        this.map.map.addSource(this.sourceID, this.sourceProps);
        this.map.map.addLayer(this.layerProps);

        for(let clusterID in this.clusterObjects) {
            const clusterObject = this.clusterObjects[clusterID];
            if(!clusterObject.active){
                continue;
            }
            clusterObject.mapMarker.addToMap();
        }
    }
}